import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ErpService } from '../services/erp.service';
import { SaludErp, NivelSalud } from '../types/erp.types';
import { TrabonesRecientes } from './TrabonesRecientes';
import { RoleService } from '../../layouts/services/role.service';
import '../styles/salud-erp.css';

/**
 * «Trabones recientes» es sólo para TI (2026-10-03): guarda 30 días del texto
 * de las consultas de TODOS los sistemas que comparten el ERP. La API sólo lo
 * sirve a superadmin y admin; a gerentes y supervisores la tabla no se les
 * muestra (en vez de enseñarles un 403).
 */
export function puedeVerTrabones(): boolean {
  return RoleService.isAdmin();
}

const REFRESCO_MS = 20000;

const TITULO: Record<NivelSalud, string> = {
  ok: 'El ERP responde con normalidad',
  atencion: 'El ERP se está congestionando',
  critico: 'La base de datos del ERP está saturada',
};

const BAJADA: Record<NivelSalud, string> = {
  ok: 'Nada que hacer. Esta pantalla se actualiza sola cada 20 segundos.',
  atencion:
    'Conviene mirar qué la está ocupando antes de que empiece a fallar la operación.',
  critico:
    'La app de tienda, la gestión de crédito y la reportería dependen de esta base. Hay que actuar ahora.',
};

function duracion(seg: number | null): string {
  if (!seg) return '—';
  if (seg < 60) return `${seg} s`;
  const m = Math.floor(seg / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/**
 * ¿La consulta parece de la app de tienda o de otro sistema?
 *
 * La comparación es SENSIBLE A MAYÚSCULAS a propósito: las consultas de la app
 * las genera Prisma desde nuestro repositorio y llevan los nombres de tabla en
 * mayúscula (`FINV`, `FALM`) con alias técnicos (`PRODUCT_ID`, `CODE_IMAGE`);
 * las de reportería vienen en minúscula (`faxinv`, `fdoc`, `fcli`) con alias de
 * negocio en español.
 *
 * La primera versión pasaba el SQL a mayúsculas antes de comparar y por eso
 * etiquetaba como «app de tienda» un reporte que usaba `finv` en minúscula —
 * una etiqueta equivocada es peor que ninguna cuando alguien está decidiendo
 * qué consulta cancelar.
 */
export function origenProbable(sql: string): 'tienda' | 'otro' {
  const tablasNuestras = /\b(FINV|FALM|FALMCAT)\b/.test(sql);
  const aliasNuestros = /(PRODUCT_ID|CODE_IMAGE|'ICOD'|'SIZE'|'STOCK'|'EAN')/.test(sql);
  return tablasNuestras && aliasNuestros ? 'tienda' : 'otro';
}

export const SaludErpScreen: React.FC = () => {
  const [salud, setSalud] = useState<SaludErp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const timer = useRef<number | null>(null);

  const cargar = useCallback(async () => {
    try {
      const s = await ErpService.getSalud();
      setSalud(s);
      setError(null);
    } catch (e) {
      // Que el monitor no pueda consultar tampoco es "sin datos": suele
      // significar que la API está peleando con el mismo problema.
      setError(e instanceof Error ? e.message : 'No se pudo consultar el ERP');
    } finally {
      setCargando(false);
      setActualizado(new Date());
    }
  }, []);

  useEffect(() => {
    void cargar();
    timer.current = window.setInterval(() => void cargar(), REFRESCO_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [cargar]);

  const nivel: NivelSalud = salud?.nivel ?? 'critico';
  const ocupacion =
    salud?.conexiones != null && salud?.maxConnections
      ? Math.round((salud.conexiones / salud.maxConnections) * 100)
      : null;

  return (
    <div className="serp">
      <div className="serp-cab">
        <div>
          <h1>Salud del ERP</h1>
          <p>
            Estado del MySQL de Proscai, del que dependen la app de tienda, la
            gestión de crédito y la reportería.
          </p>
        </div>
        <div className="serp-actualizado">
          {actualizado && (
            <>
              actualizado a las{' '}
              <b>{actualizado.toLocaleTimeString('es-MX')}</b>
            </>
          )}
          <button className="serp-refrescar" onClick={() => void cargar()}>
            Actualizar ahora
          </button>
        </div>
      </div>

      {cargando && !salud && <div className="serp-cargando">Consultando el ERP…</div>}

      {error && !salud && (
        <div className="serp-banner serp-critico">
          <div className="serp-titulo">No se pudo consultar el ERP</div>
          <div className="serp-bajada">{error}</div>
        </div>
      )}

      {salud && (
        <>
          <div className={`serp-banner serp-${nivel}`}>
            <div className="serp-etiqueta">
              {nivel === 'ok'
                ? 'TODO EN ORDEN'
                : nivel === 'atencion'
                  ? 'ATENCIÓN'
                  : 'CRÍTICO'}
            </div>
            <div className="serp-titulo">{TITULO[nivel]}</div>
            <div className="serp-bajada">{BAJADA[nivel]}</div>
            {!salud.alcanzable && (
              <div className="serp-bajada serp-fuerte">
                No se pudo abrir ni una conexión con el ERP: o está lleno o está caído.
              </div>
            )}
          </div>

          <div className="serp-cifras">
            <div className={`serp-cifra ${(ocupacion ?? 0) >= salud.umbrales.ocupacionAtencion ? 'mal' : ''}`}>
              <div className="n">{ocupacion != null ? `${ocupacion}%` : '—'}</div>
              <div className="t">
                conexiones en uso
                <br />
                <b>
                  {salud.conexiones ?? '—'} de {salud.maxConnections ?? '—'}
                </b>
              </div>
              <div className="serp-barra">
                <span style={{ width: `${Math.min(100, ocupacion ?? 0)}%` }} />
              </div>
            </div>
            <div className={`serp-cifra ${(salud.ejecutando ?? 0) >= salud.umbrales.ejecutandoAtencion ? 'mal' : ''}`}>
              <div className="n">{salud.ejecutando ?? '—'}</div>
              <div className="t">
                consultas ejecutándose
                <br />
                al mismo tiempo
              </div>
            </div>
            <div className={`serp-cifra ${(salud.masDeCincoMinutos ?? 0) >= salud.umbrales.largasAtencion ? 'mal' : ''}`}>
              <div className="n">{salud.masDeCincoMinutos ?? '—'}</div>
              <div className="t">
                llevan más de
                <br />
                cinco minutos
              </div>
            </div>
            <div className="serp-cifra">
              <div className="n">{duracion(salud.masAntiguaSegundos)}</div>
              <div className="t">
                la más antigua
                <br />
                sigue corriendo
              </div>
            </div>
          </div>

          {salud.motivos.length > 0 && (
            <div className={`serp-motivos serp-borde-${nivel}`}>
              <b>Por qué se marca así</b>
              <ul>
                {salud.motivos.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          <h2>Qué está ocupando la base ahora mismo</h2>
          <p className="serp-nota">
            Consultas de más de 30 segundos, de la más antigua a la más reciente.
            Las marcadas como <b>otro sistema</b> no vienen de la app de tienda:
            suelen ser reportería o integraciones.
          </p>

          {salud.consultas.length === 0 ? (
            <div className="serp-vacio">
              Ninguna consulta lleva más de 30 segundos. La base está trabajando al día.
            </div>
          ) : (
            <table className="serp-tabla">
              <thead>
                <tr>
                  <th>Tiempo</th>
                  <th>Origen</th>
                  <th>Base</th>
                  <th>Consulta</th>
                </tr>
              </thead>
              <tbody>
                {salud.consultas.map((c, i) => {
                  const origen = origenProbable(c.sql);
                  return (
                    <tr key={`${c.segundos}-${i}`}>
                      <td className="serp-tiempo">{duracion(c.segundos)}</td>
                      <td>
                        <span className={`serp-chip ${origen === 'tienda' ? 'tienda' : 'otro'}`}>
                          {origen === 'tienda' ? 'app de tienda' : 'otro sistema'}
                        </span>
                      </td>
                      <td className="serp-base">{c.base}</td>
                      <td className="serp-sql">{c.sql}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Lo que la foto de arriba no alcanza a ver: los trabones de 1-3 min. */}
          {puedeVerTrabones() && <TrabonesRecientes />}

          {salud.consumo && salud.consumo.rutas.length > 0 && (
            <>
              <h2>Cuánto le pide la app al ERP</h2>
              <p className="serp-nota">
                Consultas que ha generado cada pantalla desde que la API arrancó
                (
                {new Date(salud.consumo.desde).toLocaleString('es-MX', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
                ). Lo de arriba dice cómo está el ERP; esto dice qué parte es
                nuestra. <b>Cortadas</b> son las que superaron su presupuesto de
                tiempo y el servidor abortó.
              </p>
              <table className="serp-tabla">
                <thead>
                  <tr>
                    <th>Pantalla</th>
                    <th className="serp-num">Consultas</th>
                    <th className="serp-num">Cortadas</th>
                    <th className="serp-num">Reintentos</th>
                    <th className="serp-num">Tiempo medio</th>
                  </tr>
                </thead>
                <tbody>
                  {salud.consumo.rutas.slice(0, 12).map((r) => (
                    <tr key={r.ruta}>
                      <td className="serp-base">{r.ruta}</td>
                      <td className="serp-num">
                        {r.consultas.toLocaleString('es-MX')}
                      </td>
                      <td className={`serp-num ${r.cortes > 0 ? 'serp-alerta' : ''}`}>
                        {r.cortes.toLocaleString('es-MX')}
                      </td>
                      <td className="serp-num">
                        {r.reintentos.toLocaleString('es-MX')}
                      </td>
                      <td className="serp-num">
                        {Math.round(r.msEnErp / Math.max(1, r.consultas))} ms
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="serp-pie">
            El equipo recibe un correo automático cuando esto se pone en
            <b> atención</b> o <b>crítico</b>, y otro cuando se normaliza.
            {salud.rechazos != null && (
              <>
                {' '}
                El ERP lleva <b>{salud.rechazos.toLocaleString('es-MX')}</b> conexiones
                rechazadas desde que se encendió: cada una es un sistema que se quedó fuera.
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SaludErpScreen;
